import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

export const DEPLOYMENT_STATUS = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  COMPLETED_WITH_ERRORS: 'completed_with_errors',
  CANCELLED: 'cancelled',
} as const;

export type DeploymentStatus = typeof DEPLOYMENT_STATUS[keyof typeof DEPLOYMENT_STATUS];

export const DEPLOYMENT_ITEM_STATUS = {
  PENDING: 'pending',
  CREATED: 'created',
  FAILED: 'failed',
  SKIPPED: 'skipped',
} as const;

export type DeploymentItemStatus = typeof DEPLOYMENT_ITEM_STATUS[keyof typeof DEPLOYMENT_ITEM_STATUS];

export const CAMPAIGN_TYPE = {
  SCHEDULED_INTEGRATION: 'scheduled_integration',
  PERMANENT_MAINTENANCE: 'permanent_maintenance',
} as const;

export type CampaignType = typeof CAMPAIGN_TYPE[keyof typeof CAMPAIGN_TYPE];

export interface Deployment {
  id: string;
  service_desk_id: number;
  campaign_type: CampaignType;
  name: string;
  start_date: string;
  end_date: string | null;
  status: DeploymentStatus;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface DeploymentItem {
  id: number;
  deployment_id: string;
  sector: string;
  shop_sign: string;
  store_name: string;
  brand: string;
  furniture_name: string;
  technician_name: string;
  technician_email: string;
  form_template_ids: string[];
  scheduled_date: string;
  scheduled_time: string | null;
  issue_key: string | null;
  issue_id: string | null;
  status: DeploymentItemStatus;
  error_message: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
}

export interface DeploymentProgress {
  pending: number;
  created: number;
  failed: number;
  skipped: number;
  total: number;
}

export interface ValidationResult {
  errors: { line: number; column?: string; message: string }[];
  warnings: { line: number; message: string }[];
  validItems: Omit<
    DeploymentItem,
    'id' | 'deployment_id' | 'issue_key' | 'issue_id' | 'status' | 'error_message' | 'is_deleted' | 'deleted_at'
  >[];
  totalRows: number;
}

export interface DeploymentDetail extends Deployment {
  items: DeploymentItem[];
  progress: DeploymentProgress;
}

export interface JobStatus {
  id: string;
  state: string;
  progress: unknown;
  returnvalue: unknown;
  failedReason: string | null;
}

export const deploymentsApi = {
  list: async (serviceDeskId?: number, campaignType?: CampaignType): Promise<Deployment[]> => {
    const params = new URLSearchParams();
    if (serviceDeskId) params.set('service_desk_id', String(serviceDeskId));
    if (campaignType) params.set('campaign_type', campaignType);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetchWithAuth(`admin/deployments${qs}`);
    return unwrap<Deployment[]>(res, 'Failed to fetch deployments');
  },

  get: async (id: string): Promise<DeploymentDetail> => {
    const res = await fetchWithAuth(`admin/deployments/${id}`);
    return unwrap<DeploymentDetail>(res, 'Failed to fetch deployment');
  },

  create: async (input: {
    name: string;
    service_desk_id: number;
    campaign_type: CampaignType;
    start_date: string;
    end_date?: string | null;
  }): Promise<Deployment> => {
    const res = await fetchWithAuth('admin/deployments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return unwrap<Deployment>(res, 'Failed to create deployment');
  },

  delete: async (id: string): Promise<{ deleted: boolean }> => {
    const res = await fetchWithAuth(`admin/deployments/${id}`, { method: 'DELETE' });
    return unwrap<{ deleted: boolean }>(res, 'Failed to delete deployment');
  },

  validateCSV: async (id: string, file: File): Promise<ValidationResult> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetchWithAuth(`admin/deployments/${id}/validate-csv`, {
      method: 'POST',
      body: fd,
    });
    return unwrap<ValidationResult>(res, 'Failed to validate CSV');
  },

  importItems: async (id: string, items: unknown[]): Promise<{ inserted: number }> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/import-csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    return unwrap<{ inserted: number }>(res, 'Failed to import items');
  },

  deleteItem: async (id: string, itemId: number): Promise<{ deleted: boolean }> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/items/${itemId}`, { method: 'DELETE' });
    return unwrap<{ deleted: boolean }>(res, 'Failed to delete item');
  },

  execute: async (id: string): Promise<{ jobId: string; deploymentId: string }> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/execute`, { method: 'POST' });
    return unwrap<{ jobId: string; deploymentId: string }>(res, 'Failed to execute deployment');
  },

  retryItem: async (id: string, itemId: number): Promise<{ jobId: string; deploymentId: string }> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/items/${itemId}/retry`, { method: 'POST' });
    return unwrap<{ jobId: string; deploymentId: string }>(res, 'Failed to retry item');
  },

  retryFailed: async (id: string): Promise<{ jobId: string; deploymentId: string; retried: number }> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/retry-failed`, { method: 'POST' });
    return unwrap<{ jobId: string; deploymentId: string; retried: number }>(res, 'Failed to retry failed items');
  },

  jobStatus: async (id: string, jobId: string): Promise<JobStatus> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/job-status?job_id=${jobId}`);
    return unwrap<JobStatus>(res, 'Failed to fetch job status');
  },

  downloadCsvTemplate: async (id: string, rows: number = 30): Promise<Blob> => {
    const res = await fetchWithAuth(`admin/deployments/${id}/csv-template?rows=${rows}`);
    if (!res.ok) {
      let body: any = {};
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      throw new Error(body?.error || 'Failed to download CSV template');
    }
    return res.blob();
  },
};
